import { useEffect, useMemo, useState, Fragment } from "react";
import api, { fmt, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Calculator, Boxes, Layers, Pickaxe, Copy, Search } from "lucide-react";
import { Toolbar, ToolGroup, Panel, SummaryBar, Badge, EmptyState } from "@/components/ui/exec";

const TIPO_LABEL = {
  material: "Materiales", mano_obra: "Mano de Obra", herramienta: "Herramienta",
  equipo: "Equipo", seguridad_minera: "Seguridad Minera", explosivos: "Explosivos",
  acero_barrenacion: "Acero de Barrenación", basico: "Básicos / Auxiliares",
};
const TIPO_ORDER = ["material", "mano_obra", "herramienta", "equipo", "explosivos", "acero_barrenacion", "seguridad_minera", "basico"];

const blank = () => ({
  codigo: "", descripcion: "", unidad: "", es_basico: false, tipo_jornada: "8h",
  rendimiento: 1, aplicar_fasar: false, fasar_anio: null, _fasarFactor: 1,
  minero: { abundamiento: 0, factor_carga: 0, dureza: "", distancia_acarreo: 0 }, rows: [],
});

export default function APUView() {
  const { user } = useAuth();
  const canWrite = user?.role !== "consulta";
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("todos");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [insumos, setInsumos] = useState([]);
  const [basicos, setBasicos] = useState([]);
  const [fasarYears, setFasarYears] = useState([]);

  const loadList = async () => {
    const params = {};
    if (filter === "basicos") params.solo_basicos = true;
    else if (filter === "conceptos") params.solo_basicos = false;
    if (q) params.q = q;
    const { data } = await api.get("/conceptos", { params });
    setList(data);
  };
  const loadRefs = async () => {
    const [i, b, f] = await Promise.all([
      api.get("/insumos"),
      api.get("/conceptos", { params: { solo_basicos: true } }),
      api.get("/fasar"),
    ]);
    setInsumos(i.data);
    setBasicos(b.data);
    setFasarYears(f.data);
  };
  useEffect(() => { if (user) loadRefs(); /* eslint-disable-next-line */ }, [user]);
  useEffect(() => { if (!user) return; const t = setTimeout(loadList, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [filter, q, user]);

  const open = async (id) => {
    const { data } = await api.get(`/conceptos/${id}`);
    setEditing(data);
    if (data.aplicar_fasar && data.fasar_anio) fetchFactor(data.fasar_anio);
  };

  const fetchFactor = async (anio) => {
    try {
      const { data } = await api.post("/fasar/calcular", { anio, salario_diario: 1 });
      setEditing((e) => (e ? { ...e, _fasarFactor: data.factor_salario_real } : e));
    } catch { /* sin parámetros fasar */ }
  };
  const toggleFasar = (v) => {
    const anio = editing.fasar_anio || (fasarYears[0]?.anio ?? null);
    setEditing((e) => ({ ...e, aplicar_fasar: v, fasar_anio: anio }));
    if (v && anio) fetchFactor(anio);
  };
  const changeFasarYear = (y) => {
    const anio = parseInt(y);
    setEditing((e) => ({ ...e, fasar_anio: anio }));
    fetchFactor(anio);
  };
  const duplicate = async () => {
    try {
      const { data } = await api.post(`/conceptos/${editing.id}/duplicar`);
      toast.success(`Duplicado como ${data.codigo}`);
      await loadList();
      await loadRefs();
      open(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const factor = editing?.aplicar_fasar ? (editing._fasarFactor || 1) : 1;
  const eff = (r) => {
    if (r.tipo === "basico") return r.precio_efectivo ?? r.precio_unitario ?? 0;
    if (r.tipo === "mano_obra" && editing?.aplicar_fasar) return (parseFloat(r.precio_unitario) || 0) * factor;
    return parseFloat(r.precio_unitario) || 0;
  };

  const compute = useMemo(() => {
    if (!editing) return { porCat: {}, pu: 0 };
    const porCat = {};
    editing.rows.forEach((r) => {
      const e = r.tipo === "basico"
        ? (r.precio_efectivo ?? r.precio_unitario ?? 0)
        : (r.tipo === "mano_obra" && editing.aplicar_fasar)
          ? (parseFloat(r.precio_unitario) || 0) * factor
          : (parseFloat(r.precio_unitario) || 0);
      const imp = (parseFloat(r.cantidad) || 0) * e;
      porCat[r.tipo] = (porCat[r.tipo] || 0) + imp;
    });
    const pu = Object.values(porCat).reduce((a, b) => a + b, 0);
    return { porCat, pu };
  }, [editing, factor]);

  const grouped = useMemo(() => {
    if (!editing) return [];
    const map = {};
    editing.rows.forEach((r, idx) => { (map[r.tipo] = map[r.tipo] || []).push({ ...r, _idx: idx }); });
    const extra = Object.keys(map).filter((t) => !TIPO_ORDER.includes(t));
    return [...TIPO_ORDER.filter((t) => map[t]), ...extra].map((t) => ({ tipo: t, rows: map[t] }));
  }, [editing]);

  const setRow = (idx, field, value) => setEditing((e) => {
    const rows = e.rows.map((r) => ({ ...r }));
    rows[idx][field] = field === "cantidad" || field === "precio_unitario" ? (parseFloat(value) || 0) : value;
    return { ...e, rows };
  });
  const removeRow = (idx) => setEditing((e) => ({ ...e, rows: e.rows.filter((_, i) => i !== idx) }));

  const addInsumo = (ins) => setEditing((e) => ({
    ...e, rows: [...e.rows, {
      tipo: ins.familia, ref_id: ins.id, codigo: ins.codigo, descripcion: ins.descripcion,
      unidad: ins.unidad, cantidad: 1, precio_unitario: ins.precio, moneda: ins.moneda || "MXN",
    }],
  }));
  const addBasico = (b) => setEditing((e) => ({
    ...e, rows: [...e.rows, {
      tipo: "basico", ref_id: b.id, codigo: b.codigo, descripcion: b.descripcion,
      unidad: b.unidad, cantidad: 1, precio_unitario: b.pu,
    }],
  }));

  const save = async () => {
    try {
      const payload = { ...editing };
      if (editing.id) await api.put(`/conceptos/${editing.id}`, payload);
      else { const { data } = await api.post("/conceptos", payload); setEditing(data); }
      toast.success("Concepto guardado");
      loadList(); loadRefs();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar ${c.codigo}?`)) return;
    await api.delete(`/conceptos/${c.id}`);
    toast.success("Concepto eliminado");
    if (editing?.id === c.id) setEditing(null);
    loadList();
  };

  return (
    <div className="flex flex-1 min-h-0">
      {/* List */}
      <div className="w-72 shrink-0 border-r border-border flex flex-col bg-card">
        <div className="h-12 border-b border-border flex items-center px-3 gap-2">
          <Layers className="h-4 w-4 text-primary" />
          <span className="font-head font-bold text-sm uppercase tracking-tight">Conceptos APU</span>
          <div className="flex-1" />
          {canWrite && (
            <Button size="icon" className="h-7 w-7 rounded-sm" onClick={() => setEditing(blank())} data-testid="new-concepto-btn"><Plus className="h-4 w-4" /></Button>
          )}
        </div>
        <div className="p-2 flex gap-1 border-b border-border">
          <div className="relative flex-1">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="h-7 rounded-sm text-xs pl-7" data-testid="concepto-search" />
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-7 w-28 rounded-sm text-xs" data-testid="concepto-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="conceptos">Conceptos APU</SelectItem>
              <SelectItem value="basicos">Básicos</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="overflow-y-auto flex-1">
          {list.map((c) => (
            <button key={c.id} onClick={() => open(c.id)} data-testid={`concepto-item-${c.codigo}`}
              className={`exec-list-item ${editing?.id === c.id ? "active" : ""}`}>
              <div className="flex items-center gap-2">
                <span className="font-mono-num text-xs text-primary font-semibold">{c.codigo}</span>
                {c.es_basico && <Badge>Básico</Badge>}
                {c.tipo_jornada === "12h" && <Pickaxe className="h-3 w-3 text-copper" title="Turno minero 12h" />}
              </div>
              <div className="text-xs truncate">{c.descripcion}</div>
              <div className="font-mono-num text-xs text-muted-foreground mt-0.5">$ {fmt(c.pu, 2)} / {c.unidad}</div>
            </button>
          ))}
          {list.length === 0 && <div className="p-4 text-xs text-muted-foreground">Sin conceptos.</div>}
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 min-w-0 min-h-0 flex flex-col">
        {!editing ? (
          <EmptyState icon={Layers} title="Selecciona o crea un concepto" text="El análisis de precio unitario integra materiales, mano de obra con FASAR, equipo, herramienta, explosivos, acero de barrenación y básicos auxiliares con precisión de 6 decimales."
            action={canWrite && <Button size="sm" className="rounded-sm h-8 gap-1" onClick={() => setEditing(blank())} data-testid="empty-new-concepto-btn"><Plus className="h-3.5 w-3.5" /> Nuevo concepto</Button>} testId="apu-empty" />
        ) : (
          <>
          <div className="flex-1 min-h-0 overflow-auto p-4 space-y-3">
            {/* Ribbon */}
            <Toolbar testId="apu-toolbar">
              <ToolGroup label="Concepto">
                <div className="flex gap-2">
                  <div><Label className="text-[9px] uppercase tracking-wider text-muted-foreground">Código</Label><Input disabled={!canWrite} className="mt-0.5 h-8 w-28 font-mono-num text-xs" value={editing.codigo} onChange={(e) => setEditing({ ...editing, codigo: e.target.value })} data-testid="apu-codigo" /></div>
                  <div><Label className="text-[9px] uppercase tracking-wider text-muted-foreground">Descripción</Label><Input disabled={!canWrite} className="mt-0.5 h-8 w-72 text-xs" value={editing.descripcion} onChange={(e) => setEditing({ ...editing, descripcion: e.target.value })} data-testid="apu-descripcion" /></div>
                  <div><Label className="text-[9px] uppercase tracking-wider text-muted-foreground">Unidad</Label><Input disabled={!canWrite} className="mt-0.5 h-8 w-20 font-mono-num text-xs" value={editing.unidad} onChange={(e) => setEditing({ ...editing, unidad: e.target.value })} data-testid="apu-unidad" /></div>
                </div>
              </ToolGroup>
              {canWrite && (
                <ToolGroup label="Matriz">
                  <InsumoPicker insumos={insumos} onPick={addInsumo} />
                  <BasicoPicker basicos={basicos.filter((b) => b.id !== editing.id)} onPick={addBasico} />
                </ToolGroup>
              )}
              {canWrite && (
                <ToolGroup label="Acciones">
                  <Button size="sm" className="h-8 rounded-sm gap-1" onClick={save} data-testid="apu-save"><Save className="h-4 w-4" /> Guardar</Button>
                  {editing.id && <Button size="sm" variant="outline" className="h-8 rounded-sm gap-1" onClick={duplicate} data-testid="apu-duplicate"><Copy className="h-4 w-4" /> Duplicar</Button>}
                  {editing.id && <Button size="sm" variant="outline" className="h-8 rounded-sm text-destructive" onClick={() => remove(editing)} data-testid="apu-delete"><Trash2 className="h-4 w-4" /></Button>}
                </ToolGroup>
              )}
            </Toolbar>

            {/* Params row */}
            <div className="exec-panel flex flex-wrap gap-4 items-end p-3">
              <div className="flex items-center gap-2">
                <Switch disabled={!canWrite} checked={editing.es_basico} onCheckedChange={(v) => setEditing({ ...editing, es_basico: v })} data-testid="apu-es-basico" />
                <Label className="text-xs">Es básico / auxiliar</Label>
              </div>
              <div>
                <Label className="text-[10px]">Jornada</Label>
                <Select value={editing.tipo_jornada} onValueChange={(v) => setEditing({ ...editing, tipo_jornada: v })} disabled={!canWrite}>
                  <SelectTrigger className="mt-0.5 h-8 w-40 rounded-sm" data-testid="apu-jornada"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="8h">Jornada 8 h</SelectItem>
                    <SelectItem value="12h">Turno minero 12 h</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[10px]">Rendimiento ({editing.unidad || "u"}/{editing.tipo_jornada})</Label>
                <Input disabled={!canWrite} type="number" step="any" className="mt-0.5 h-8 w-28 font-mono-num text-right" value={editing.rendimiento} onChange={(e) => setEditing({ ...editing, rendimiento: parseFloat(e.target.value) || 0 })} data-testid="apu-rendimiento" />
              </div>
              <div className="flex items-center gap-2 border-l border-border pl-4">
                <Switch disabled={!canWrite} checked={editing.aplicar_fasar} onCheckedChange={toggleFasar} data-testid="apu-aplicar-fasar" />
                <Label className="text-xs">FASAR a mano de obra</Label>
              </div>
              {editing.aplicar_fasar && (
                <div>
                  <Label className="text-[10px]">Año FASAR</Label>
                  <Select value={String(editing.fasar_anio || "")} onValueChange={changeFasarYear} disabled={!canWrite}>
                    <SelectTrigger className="mt-0.5 h-8 w-24 rounded-sm font-mono-num" data-testid="apu-fasar-anio"><SelectValue placeholder="Año" /></SelectTrigger>
                    <SelectContent>
                      {fasarYears.map((y) => <SelectItem key={y.anio} value={String(y.anio)} className="font-mono-num">{y.anio}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {editing.aplicar_fasar && <span className="font-mono-num text-xs text-amber-500 self-end pb-2" data-testid="apu-fasar-factor">Factor SR: {fmt(factor, 4)}</span>}
            </div>

            {/* Campos mineros */}
            <div className="exec-panel border-copper/40">
              <div className="exec-panel-head !text-copper">
                <Pickaxe className="h-3.5 w-3.5" /> Campos Mineros
              </div>
              <div className="p-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div><Label className="text-[10px]">Abundamiento (%)</Label><Input disabled={!canWrite} type="number" step="any" className="mt-0.5 h-8 font-mono-num text-right" value={editing.minero.abundamiento} onChange={(e) => setEditing({ ...editing, minero: { ...editing.minero, abundamiento: parseFloat(e.target.value) || 0 } })} data-testid="apu-abundamiento" /></div>
                <div><Label className="text-[10px]">Factor de carga (kg/m³)</Label><Input disabled={!canWrite} type="number" step="any" className="mt-0.5 h-8 font-mono-num text-right" value={editing.minero.factor_carga} onChange={(e) => setEditing({ ...editing, minero: { ...editing.minero, factor_carga: parseFloat(e.target.value) || 0 } })} data-testid="apu-factor-carga" /></div>
                <div><Label className="text-[10px]">Dureza</Label><Input disabled={!canWrite} className="mt-0.5 h-8" value={editing.minero.dureza} onChange={(e) => setEditing({ ...editing, minero: { ...editing.minero, dureza: e.target.value } })} data-testid="apu-dureza" /></div>
                <div><Label className="text-[10px]">Dist. acarreo (m)</Label><Input disabled={!canWrite} type="number" step="any" className="mt-0.5 h-8 font-mono-num text-right" value={editing.minero.distancia_acarreo} onChange={(e) => setEditing({ ...editing, minero: { ...editing.minero, distancia_acarreo: parseFloat(e.target.value) || 0 } })} data-testid="apu-distancia" /></div>
              </div>
            </div>

            {/* Matriz */}
            <div className="grid lg:grid-cols-4 gap-4">
              <Panel className="lg:col-span-3 overflow-hidden" title="Matriz de análisis" icon={Boxes}>
                <table className="qc-table">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Código</th>
                      <th>Descripción</th>
                      <th style={{ width: 55 }}>Unidad</th>
                      <th style={{ width: 90 }} className="text-right">Cantidad</th>
                      <th style={{ width: 110 }} className="text-right">P.U.</th>
                      <th style={{ width: 110 }} className="text-right">Importe</th>
                      <th style={{ width: 28 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {grouped.map((g) => (
                      <Fragment key={g.tipo}>
                        <tr className="!bg-primary/5 border-l-2 border-l-primary">
                          <td colSpan={6} className="text-[10px] uppercase tracking-[0.12em] text-primary font-semibold py-1">{TIPO_LABEL[g.tipo] || g.tipo} <span className="font-mono-num text-muted-foreground ml-2 normal-case tracking-normal">{fmt(compute.porCat[g.tipo], 4)}</span></td>
                          <td></td>
                        </tr>
                        {g.rows.map((r) => (
                          <tr key={r._idx} data-testid={`apu-row-${r.codigo}`}>
                            <td className="font-mono-num text-primary text-xs">{r.codigo}</td>
                            <td className="text-xs">{r.descripcion}</td>
                            <td className="font-mono-num text-muted-foreground text-xs">{r.unidad}</td>
                            <td className="text-right">
                              <input disabled={!canWrite} type="number" step="any" value={r.cantidad} onChange={(e) => setRow(r._idx, "cantidad", e.target.value)}
                                className="w-full text-right font-mono-num bg-transparent outline-none focus:bg-background focus:border focus:border-primary rounded-sm px-1" data-testid={`apu-cant-${r.codigo}`} />
                            </td>
                            <td className="text-right">
                              {r.tipo === "basico" ? (
                                <span className="font-mono-num text-muted-foreground">{fmt(eff(r), 4)}</span>
                              ) : (
                                <div className="flex items-center justify-end gap-1">
                                  <input disabled={!canWrite} type="number" step="any" value={r.precio_unitario} onChange={(e) => setRow(r._idx, "precio_unitario", e.target.value)}
                                    className="w-full text-right font-mono-num bg-transparent outline-none focus:bg-background focus:border focus:border-primary rounded-sm px-1 disabled:opacity-70" />
                                  <select disabled={!canWrite} value={r.moneda || "MXN"} onChange={(e) => setRow(r._idx, "moneda", e.target.value)}
                                    className="shrink-0 text-[9px] font-mono-num bg-transparent border border-border rounded-sm px-0.5 py-0.5 outline-none disabled:opacity-70" data-testid={`apu-moneda-${r.codigo}`}>
                                    <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
                                  </select>
                                </div>
                              )}
                              {r.tipo === "mano_obra" && editing.aplicar_fasar && <div className="text-[9px] text-amber-500 font-mono-num">×{fmt(factor, 4)} = {fmt(eff(r), 2)}</div>}
                            </td>
                            <td className="text-right font-mono-num">{fmt((parseFloat(r.cantidad) || 0) * eff(r), 4)}</td>
                            <td className="text-center">{canWrite && <button className="text-destructive" onClick={() => removeRow(r._idx)}><X className="h-3.5 w-3.5" /></button>}</td>
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                    {editing.rows.length === 0 && <tr><td colSpan={7} className="text-center text-muted-foreground py-8 text-xs">Agrega insumos o básicos a la matriz desde la barra de herramientas.</td></tr>}
                  </tbody>
                </table>
              </Panel>

              {/* Summary */}
              <div className="space-y-3">
                <Panel title="Costo directo" icon={Calculator} tone="primary" className="h-fit">
                  <table className="qc-table">
                    <tbody>
                      {TIPO_ORDER.filter((t) => compute.porCat[t]).map((t) => (
                        <tr key={t}><td className="text-xs">{TIPO_LABEL[t]}</td><td className="text-right font-mono-num">{fmt(compute.porCat[t], 4)}</td></tr>
                      ))}
                      <tr className="!bg-primary/10"><td className="font-semibold">P.U. ({editing.unidad || "u"})</td><td className="text-right font-mono-num font-semibold text-primary" data-testid="apu-pu-total">$ {fmt(compute.pu, 6)}</td></tr>
                    </tbody>
                  </table>
                </Panel>
                <Panel title="Incidencia por categoría">
                  <div className="p-3 space-y-2">
                    {TIPO_ORDER.filter((t) => compute.porCat[t]).map((t) => (
                      <div key={t}>
                        <div className="flex justify-between text-[11px] mb-0.5"><span>{TIPO_LABEL[t]}</span><span className="font-mono-num text-muted-foreground">{fmt(compute.pu > 0 ? (compute.porCat[t] / compute.pu) * 100 : 0, 1)}%</span></div>
                        <div className="h-1.5 bg-muted rounded-sm"><div className={`h-1.5 rounded-sm ${t === "mano_obra" ? "bg-copper" : "bg-primary"}`} style={{ width: `${compute.pu > 0 ? (compute.porCat[t] / compute.pu) * 100 : 0}%` }} /></div>
                      </div>
                    ))}
                    {compute.pu === 0 && <div className="text-[11px] text-muted-foreground">Sin renglones.</div>}
                  </div>
                </Panel>
              </div>
            </div>
          </div>
          <SummaryBar testId="apu-summary-bar" items={[
            { label: "Renglones", value: String(editing.rows.length) },
            { label: "Jornada", value: editing.tipo_jornada === "12h" ? "Turno 12 h" : "8 h" },
            { label: "Rendimiento", value: `${fmt(editing.rendimiento, 4)} ${editing.unidad || "u"}` },
            { label: "Factor FASAR", value: editing.aplicar_fasar ? fmt(factor, 4) : "—" },
            { label: `Precio unitario · ${editing.unidad || "u"}`, value: `$ ${fmt(compute.pu, 6)}`, highlight: true, testId: "sb-apu-pu" },
          ]} />
          </>
        )}
      </div>
    </div>
  );
}

function InsumoPicker({ insumos, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 rounded-sm gap-1" data-testid="add-insumo-row-btn"><Plus className="h-3.5 w-3.5" /> Insumo</Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <Command>
          <CommandInput placeholder="Buscar insumo…" data-testid="insumo-picker-input" />
          <CommandList>
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {insumos.map((i) => (
                <CommandItem key={i.id} value={`${i.codigo} ${i.descripcion}`} onSelect={() => { onPick(i); setOpen(false); }} data-testid={`insumo-pick-${i.codigo}`}>
                  <span className="font-mono-num text-primary text-xs mr-2">{i.codigo}</span>
                  <span className="text-xs flex-1 truncate">{i.descripcion}</span>
                  <span className="font-mono-num text-xs text-muted-foreground ml-2">${fmt(i.precio, 2)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function BasicoPicker({ basicos, onPick }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 rounded-sm gap-1" data-testid="add-basico-row-btn"><Layers className="h-3.5 w-3.5" /> Básico</Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <Command>
          <CommandInput placeholder="Buscar básico…" />
          <CommandList>
            <CommandEmpty>Sin básicos.</CommandEmpty>
            <CommandGroup>
              {basicos.map((b) => (
                <CommandItem key={b.id} value={`${b.codigo} ${b.descripcion}`} onSelect={() => { onPick(b); setOpen(false); }} data-testid={`basico-pick-${b.codigo}`}>
                  <span className="font-mono-num text-primary text-xs mr-2">{b.codigo}</span>
                  <span className="text-xs flex-1 truncate">{b.descripcion}</span>
                  <span className="font-mono-num text-xs text-muted-foreground ml-2">${fmt(b.pu, 2)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
